// The personal slice of the roadmap (kanban 5895aca3): the questions Merlin
// addresses to the signed-in person (Answer / Record answer / Dismiss — the
// same controls as the admin tab), the initiatives they are named on, and
// the org chart read-only for leadership.
//
// Since 2026-10-15 (owner ask: "everyone sees the roadmap for all teams")
// this is the FALLBACK for an account WITHOUT roadmap:read; every reader
// gets the whole roadmap instead (MyRoadmapPage → RoadmapReportsPage, with
// the "Mine" filter). The digest emails link to /portal/my-roadmap either way.

import { useCallback, useEffect, useState } from 'react';
import { myRoadmapApi, type MyRoadmapResponse } from '../../services/myRoadmap';
import type { QuestionIdentity, QuestionResponseRequest } from '../../services/roadmapReports';
import { QuestionResponseControls } from '../roadmap/QuestionResponseControls';
import { EvidenceList } from '../roadmap/EvidenceList';
import { OrgChartPanel } from '../roadmap/OrgChartPanel';
import { questionWithoutDecideBy, ownerTeamOf, relatedTeamsOf } from '../roadmap/roadmapViewUtils';
import styles from '../roadmap/RoadmapV2.module.css';

const STATUS_LABEL: Record<string, string> = {
  proposed: 'Proposed', in_progress: 'In progress', blocked: 'Blocked', waiting: 'Waiting',
  done: 'Done', dropped: 'Dropped', unknown: 'Status unknown',
};

export function MyRoadmapSlice() {
  const [data, setData] = useState<MyRoadmapResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped after a write; the effect re-reads. State is set only from the
  // fetch's callbacks (never synchronously in the effect body).
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    myRoadmapApi
      .get()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your roadmap — try again in a moment.');
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const respond = useCallback(async (req: QuestionResponseRequest) => {
    await myRoadmapApi.respond(req);
    setVersion((v) => v + 1);
  }, []);
  const withdraw = useCallback(async (id: QuestionIdentity) => {
    await myRoadmapApi.withdraw(id);
    setVersion((v) => v + 1);
  }, []);

  if (error) return <div className={styles.page}><p className={styles.respondError} role="alert">{error}</p></div>;
  if (!data) return <div className={styles.page}><p className={styles.muted}>Loading…</p></div>;

  const open = data.questions.filter((q) => !q.response).length;

  return (
    <div className={styles.page} data-testid="my-roadmap-slice">
      <header className={styles.myHead}>
        <h1 className={styles.myTitle}>My roadmap</h1>
        <p className={styles.muted}>
          {data.me.length === 0
            ? 'The roadmap does not name you yet. When Merlin asks you something it shows up here.'
            : `What Merlin needs from you and the initiatives you are on.${data.report_date ? ` As of the roadmap run on ${data.report_date}.` : ''}`}
        </p>
      </header>

      <section className={styles.panel} aria-label="Your questions">
        <div className={styles.panelHead}>
          <h2>Merlin's questions for you</h2>
          <span className={styles.meta}>{open} open · answer by typing or recording, or dismiss what doesn't matter. Merlin picks it up on its next run.</span>
        </div>
        {data.questions.length === 0 ? (
          <p className={styles.muted}>Nothing for you right now.</p>
        ) : (
          <ol className={styles.askCards}>
            {data.questions.map((q) => (
              <li key={`${q.initiative_key}:${q.question}`} className={styles.askCard}>
                <div className={styles.askQuestion}>{questionWithoutDecideBy(q.question)}</div>
                {/* The words the question rests on (kanban 9eb5f005). Sources
                    are named, not linked: the viewer lives on the leadership-only /roadmap. */}
                <EvidenceList evidence={q.evidence ?? []} aria-label="What this question rests on" />
                <div className={styles.askCardFoot}>
                  <span className={styles.myInitiative}>{q.title}</span>
                </div>
                <QuestionResponseControls
                  identity={{ initiative_key: q.initiative_key, question: q.question }}
                  response={q.response}
                  onRespond={respond}
                  onWithdraw={withdraw}
                />
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className={styles.panel} aria-label="Your initiatives">
        <div className={styles.panelHead}>
          <h2>Initiatives you're on</h2>
          <span className={styles.meta}>{data.initiatives.length}</span>
        </div>
        {data.initiatives.length === 0 ? (
          <p className={styles.muted}>None yet.</p>
        ) : (
          <ul className={styles.myList}>
            {data.initiatives.map((i) => (
              <li key={i.initiative_key} className={styles.myItem}>
                <div className={styles.myItemHead}>
                  <strong>{i.title}</strong>
                  <span className={styles.orgTeam}>{STATUS_LABEL[i.status] ?? i.status}</span>
                  {i.target_date && <span className={styles.meta}>target {i.target_date}</span>}
                  {ownerTeamOf(i) && <span className={styles.orgTeam} title="Owning task force">{ownerTeamOf(i)}</span>}
                  {relatedTeamsOf(i).map((t) => (
                    <span key={t} className={styles.orgTeam} title="Related task force — has a stake, does not own it">
                      {t} (related)
                    </span>
                  ))}
                </div>
                {i.summary && <p className={styles.mySummary}>{i.summary}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.org && <OrgChartPanel data={data.org} />}
    </div>
  );
}
